import React from "react";
import { Chart as ChartJS, registerables } from "chart.js";
import { Line } from "react-chartjs-2";
import { ROLE_LABELS } from "../../../shared/util/roles";

ChartJS.register(...registerables);

// Chart.js draws on a canvas and can't read CSS variables; these mirror
// index.css.
export const COLORS = {
  accent: "#00d97e",
  blue:   "#00a8ff",
  danger: "#ff4757",
  text:   "#ccc",
  muted:  "#888",
  dim:    "#555",
  grid:   "#1a1a20",
};

// ─── Formatting ───────────────────────────────────────────────────────────────

export function formatChf(amount, digits = 0) {
  return formatMoney(amount, "CHF", digits);
}

// e.g. the fund's AUM, which is in USD
export function formatMoney(amount, currency, digits = 0) {
  return new Intl.NumberFormat("en-CH", {
    style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(amount || 0);
}

export function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

// "just now", "12 min ago", "3 h ago", "2 d ago", then the date
export function formatRelative(iso, now = Date.now()) {
  if (!iso) return "never";
  const minutes = Math.round((now - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h ago`;
  if (minutes < 14 * 24 * 60) return `${Math.round(minutes / 1440)} d ago`;
  return formatDate(iso);
}

// Small samples make percentages misleading, so they're shown only from 10 up
function deltaText(value, previous) {
  const diff = value - previous;
  if (diff === 0) return "±0";
  const sign = diff > 0 ? "▲" : "▼";
  const pct = previous >= 10 ? ` (${Math.round((Math.abs(diff) / previous) * 100)} %)` : "";
  return `${sign} ${Math.abs(Math.round(diff * 100) / 100)}${pct}`;
}

// ─── Building blocks ──────────────────────────────────────────────────────────

export function Delta({ value, previous, goodWhen = "up", format = (x) => x }) {
  if (previous == null) return null;
  const diff = value - previous;
  const tone = diff === 0 ? "flat" : (diff > 0) === (goodWhen === "up") ? "good" : "bad";
  const text = deltaText(value, previous);
  return (
    <span className={`adm-delta adm-delta--${tone}`} title={`Previous period: ${format(previous)}`}>
      {text}
    </span>
  );
}

export function Sparkline({ values, color = COLORS.accent }) {
  if (!values || values.length < 2) return <div className="adm-spark" />;
  return (
    <div className="adm-spark" aria-hidden="true">
      <Line
        data={{
          labels: values.map((_, i) => i),
          datasets: [{ data: values, borderColor: color, borderWidth: 1.5, pointRadius: 0, tension: 0.3, fill: false }],
        }}
        options={{
          responsive: true, maintainAspectRatio: false, animation: false,
          plugins: { legend: { display: false }, tooltip: { enabled: false } },
          scales: { x: { display: false }, y: { display: false } },
        }}
      />
    </div>
  );
}

export function KpiCard({ label, value, delta, sub, spark, sparkColor }) {
  return (
    <div className="adm-kpi">
      <span className="adm-kpi__label">{label}</span>
      <div className="adm-kpi__row">
        <span className="adm-kpi__value">{value}</span>
        {delta}
      </div>
      {sub && <span className="adm-kpi__sub">{sub}</span>}
      {spark && <Sparkline values={spark} color={sparkColor} />}
    </div>
  );
}

export const STATUS_LABELS = {
  active:        "Active",
  ending:        "Renews soon",
  complimentary: "Complimentary",
  expired:       "Expired",
  never:         "Never paid",
};

export function StatusChip({ status }) {
  return <span className={`adm-chip adm-chip--${status}`}>{STATUS_LABELS[status] || status}</span>;
}

// "admin" or "portfolio manager" next to a name; nothing for a plain user
export function RoleChip({ role }) {
  if (role !== "admin" && role !== "portfolio_manager") return null;
  return <span className={`adm-role adm-role--${role}`}>{ROLE_LABELS[role]}</span>;
}

// "Chrome on macOS" from a user agent string
export function describeDevice(userAgent) {
  if (!userAgent) return "Unknown device";
  const ua = userAgent;
  const browser = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Firefox\//.test(ua) ? "Firefox"
    : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : /curl|python|node|axios/i.test(ua) ? "Script" : "Browser";
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "macOS"
    : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : null;
  return os ? `${browser} on ${os}` : browser;
}

export function Skeleton({ className = "" }) {
  return <span className={`adm-skeleton ${className}`} />;
}

// ─── CSV export ───────────────────────────────────────────────────────────────

export function downloadCsv(filename, header, rows) {
  const cell = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(cell).join(",")).join("\n");
  // The BOM makes Excel read the file as UTF-8
  const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function CsvButton({ onClick }) {
  return <button type="button" className="adm-btn adm-btn--outline adm-btn--small" onClick={onClick}>Export CSV</button>;
}
